import { Chart, useChart } from '@chakra-ui/charts';
import { Box, Center, HStack, Text, useToken, VStack } from '@chakra-ui/react';
import ChartWrapper from '@components/ui/chart/ChartWrapper';
import { nfzColor } from '@utils/map/map.constants';
import { FormattedMessage, useIntl } from 'react-intl';
import { Label, Pie, PieChart, PieLabelRenderProps, Sector } from 'recharts';

import { DroneAlertLevelEnum } from '@/types/data/data.types';
import { LevelDroneCountType } from '@/types/data/statistic.types';

type Props = {
  data: LevelDroneCountType[];
};

const CustomizedPieLabel = ({ x, y, value }: PieLabelRenderProps) => {
  const [neutral500] = useToken('colors', ['neutral.500']);
  const [fontStandard] = useToken('fontSizes', ['sm']);

  if (value === 0) {
    return null;
  }
  return (
    <text x={x} y={y} fill={neutral500} style={{ fontSize: fontStandard }} textAnchor="middle">
      {value}
    </text>
  );
};

const CustomLabel = ({ total }: Readonly<{ total: number }>) => {
  return (
    <foreignObject width="100%" height="100%">
      <Center width="100%" height="100%">
        <VStack gap={0.25}>
          <Text fontSize={24} fontWeight="medium">
            {total}
          </Text>
          <Text color="neutral.300" whiteSpace="pre-line" textAlign="center">
            <FormattedMessage id="components.notificationSearch.reporting.totalIntrusion" />
          </Text>
        </VStack>
      </Center>
    </foreignObject>
  );
};

export default function DroneInAlertZonePie({ data }: Readonly<Props>) {
  const { formatMessage } = useIntl();

  const chart = useChart({
    data: data.map((entry) => ({
      name: formatMessage({
        id: `notification.criticality.${entry.level}`,
      }),
      level: entry.level,
      value: entry.count,
      color: nfzColor[entry.level].fill,
    })),
  });

  return (
    <ChartWrapper
      width="410px"
      isEmpty={chart.getTotal('value') === 0}
      emptyLabel={formatMessage({ id: 'components.notificationSearch.reporting.noDetection' })}
      title={formatMessage({ id: 'components.notificationSearch.reporting.droneInAlertZone' })}
    >
      <Chart.Root chart={chart} style={{ position: 'relative' }}>
        <PieChart width="100%" height={348}>
          <Pie
            innerRadius={102}
            outerRadius={124}
            startAngle={90}
            endAngle={-270}
            label={CustomizedPieLabel}
            data={chart.data}
            nameKey="name"
            strokeWidth={0}
            shape={(props) => <Sector {...props} fill={chart.color(props.payload.color)} />}
          />
          <Label position="center" content={() => <CustomLabel total={chart.getTotal('value')} />} />
        </PieChart>
      </Chart.Root>
      <HStack gap={2} width="100%" justifyContent="center" flexWrap="wrap">
        {chart.data
          .toSorted(
            (a, b) =>
              Object.keys(DroneAlertLevelEnum).indexOf(a.level) - Object.keys(DroneAlertLevelEnum).indexOf(b.level),
          )
          .map((entry) => {
            return (
              <HStack key={entry.value} gap={1}>
                <Box
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    backgroundColor: entry.color,
                    flexShrink: 0,
                  }}
                />
                <Text textStyle="sm" lineClamp={2}>
                  {entry.name}
                </Text>
              </HStack>
            );
          })}
      </HStack>
    </ChartWrapper>
  );
}
